/**
 * CHECKOUT SUCCESS PAGE
 * 
 * Displays order confirmation after successful payment
 * Shows order details, shipping info, and next steps
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/checkout-session/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Failed to retrieve order details');
        }

        const data = await response.json();
        setOrderData(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching order details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [sessionId]);

  const formatPrice = (cents, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="checkout-success loading">
        <div className="spinner"></div>
        <p>Loading your order details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-success error">
        <h1>⚠️ Error</h1>
        <p>{error}</p>
        <Link to="/" className="button-primary">Return to Home</Link>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="checkout-success error">
        <h1>Order Not Found</h1>
        <p>We couldn't find your order details.</p>
        <Link to="/" className="button-primary">Return to Home</Link>
      </div>
    );
  }

  return (
    <div className="checkout-success">
      <div className="success-header">
        <div className="success-icon">✓</div>
        <h1>Order Confirmed!</h1>
        <p className="success-message">
          Thank you for your purchase, {orderData.customerName}!
        </p>
      </div>

      <div className="order-details-container">
        {/* Order Summary */}
        <div className="order-section">
          <h2>Order Summary</h2>
          <div className="order-items">
            {orderData.lineItems.map((item, index) => (
              <div key={index} className="order-item">
                <div className="item-details">
                  <span className="item-name">{item.description}</span>
                  <span className="item-quantity">Qty: {item.quantity}</span>
                </div>
                <span className="item-price">
                  {formatPrice(item.amount_total, orderData.currency)}
                </span>
              </div>
            ))}
          </div>

          <div className="order-totals">
            <div className="total-row">
              <span>Subtotal:</span>
              <span>{formatPrice(orderData.amountSubtotal, orderData.currency)}</span>
            </div>
            {orderData.totalShipping > 0 && (
              <div className="total-row">
                <span>Shipping:</span>
                <span>{formatPrice(orderData.totalShipping, orderData.currency)}</span>
              </div>
            )}
            {orderData.totalTax > 0 && (
              <div className="total-row">
                <span>Tax:</span>
                <span>{formatPrice(orderData.totalTax, orderData.currency)}</span>
              </div>
            )}
            <div className="total-row final">
              <span>Total:</span>
              <span>{formatPrice(orderData.amountTotal, orderData.currency)}</span>
            </div>
          </div>
        </div>

        {/* Shipping Information */}
        {orderData.shippingAddress && (
          <div className="order-section">
            <h2>Shipping Information</h2>
            <div className="shipping-details">
              <p className="shipping-name">{orderData.shippingName}</p>
              <p>{orderData.shippingAddress.line1}</p>
              {orderData.shippingAddress.line2 && (
                <p>{orderData.shippingAddress.line2}</p>
              )}
              <p>
                {orderData.shippingAddress.city}, {orderData.shippingAddress.state}{' '}
                {orderData.shippingAddress.postal_code}
              </p>
              <p>{orderData.shippingAddress.country}</p>
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="order-section">
          <h2>Confirmation Email</h2>
          <p>
            We've sent a confirmation email to{' '}
            <strong>{orderData.customerEmail}</strong>
          </p>
          <p className="email-note">
            You'll receive tracking information once your order ships.
          </p>
        </div>

        {/* Next Steps */}
        <div className="order-section next-steps">
          <h2>What's Next?</h2>
          <div className="steps-list">
            <div className="step">
              <span className="step-number">1</span>
              <div className="step-content">
                <h3>Order Processing</h3>
                <p>Your order is being prepared for shipment</p>
              </div>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <div className="step-content">
                <h3>Shipment</h3>
                <p>You'll receive tracking info within 1-2 business days</p>
              </div>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <div className="step-content">
                <h3>Activation</h3>
                <p>Set up your Graffiti™ Scrambler unit and activate your license</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="success-actions">
        <Link to="/" className="button-secondary">
          Continue Shopping
        </Link>
        <Link to="/account/orders" className="button-primary">
          View My Orders
        </Link>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
